import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, useFormikContext } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  LinearProgress,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip,
  InputAdornment,
  IconButton,
  Switch,
} from "@material-ui/core";
import { SettingsEthernet, Visibility, VisibilityOff } from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { logError, logInfo } from "../../utils/logger";
import { getTokens, INK } from "../../styles/tokens";
import {
  usePanelStyles,
  ModalTitle,
  FormField,
  FormGrid,
  FormSection,
  SwitchRow,
  Callout,
} from "./panelKit";
import {
  ERP_TYPES,
  ERP_INTEGRATION_TYPES,
  ISP_IA_MODELS,
  ISP_IA_DEFAULTS,
  SGP_PASSWORD_OPTIONS,
  SGP_MODES,
  SGP_DEFAULT_MODE,
  SGP_JSON_FIELDS,
  parseSgpJsonContent,
  buildSgpJsonContent,
  normalizeSgpMode,
  validateSgpConfig,
  validateIxcConfig,
  validateAsaasConfig,
  ASAAS_JSON_FIELDS,
  ASAAS_ENVIRONMENTS,
  validateIspIaConfig,
  HOSPEDAGEM_PROVIDERS,
  HOSPEDAGEM_IA_DEFAULTS,
  validateHospedagemConectorConfig,
  validateHospedagemIaConfig,
} from "../../utils/erp";
import {
  HOTSPOT_JSON_FIELDS,
  HOTSPOT_TABELAS,
  validateHotspotConfig,
} from "../../utils/hotspot";
import HospedagemVendedoraFields from "./HospedagemVendedoraFields";
import {
  VENDEDORA_DEFAULTS,
  vendedoraFromJson,
  applyVendedoraFields,
  conectorPropostaFromJson,
  applyConectorPropostaField,
  validateVendedora,
} from "./hospedagemVendedora";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // "Testar conexão" em destaque: faixa própria no fim da seção de conexão
    testStrip: {
      gridColumn: "1 / -1",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      padding: "10px 12px 10px 14px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
    },
    testText: { minWidth: 0, flex: "1 1 220px" },
    testTitle: {
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.mode === "dark" ? t.text : INK[800],
    },
    loadingBar: { flex: "none", height: 2, marginBottom: -2 },
    groupHeader: {
      pointerEvents: "none",
      lineHeight: "30px",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: t.textMuted,
      backgroundColor: t.paper,
    },
  };
});

const DialogflowSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  // projectName: Yup.string()
  //   .min(3, "Too Short!")
  //   .max(100, "Too Long!")
  //   .required(),
  // jsonContent: Yup.string().min(3, "Too Short!").required(),
  // language: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required(),
});

const VOICES = ["alloy", "nova", "shimmer", "echo", "onyx", "fable", "coral", "sage"];

// Campo de texto do formulário: rótulo acima, erro do Formik logo abaixo.
const TextInput = ({ name, label, help, required, full, ...props }) => {
  const { touched, errors, values, submitCount } = useFormikContext();
  // campo vazio só acusa "obrigatório" depois de tentar salvar (o foco inicial já o marca como tocado)
  const error =
    touched[name] && (submitCount > 0 || Boolean(values[name])) && errors[name];
  return (
    <FormField label={label} help={help} required={required} error={error} full={full}>
      <Field
        as={TextField}
        name={name}
        required={required}
        error={Boolean(error)}
        variant="outlined"
        size="small"
        fullWidth
        {...props}
      />
    </FormField>
  );
};

const SelectInput = ({ name, label, help, required, full, children, ...props }) => {
  const { touched, errors } = useFormikContext();
  const error = touched[name] && errors[name];
  return (
    <FormField label={label} help={help} required={required} error={error} full={full}>
      <Field
        as={TextField}
        select
        name={name}
        required={required}
        error={Boolean(error)}
        variant="outlined"
        size="small"
        fullWidth
        SelectProps={{ displayEmpty: true }}
        {...props}
      >
        {children}
      </Field>
    </FormField>
  );
};

// Segredo (token/chave): mascarado por padrão, com botão para conferir o que foi digitado.
const SecretInput = ({ visible, onToggle, ...props }) => (
  <TextInput
    type={visible ? "text" : "password"}
    autoComplete="new-password"
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            size="small"
            edge="end"
            aria-label={i18n.t("queueIntegrationModal.form.sgpTokenToggle")}
            onClick={onToggle}
          >
            {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </InputAdornment>
      ),
    }}
    {...props}
  />
);

const SwitchInput = ({ name, label, help, full }) => (
  <Field name={name}>
    {({ field }) => (
      <SwitchRow label={label} help={help} full={full}>
        <Switch
          checked={Boolean(field.value)}
          onChange={(e) =>
            field.onChange({
              target: { name, value: e.target.checked },
            })
          }
          color="primary"
          inputProps={{ "aria-label": label }}
        />
      </SwitchRow>
    )}
  </Field>
);

// Fields that live inside jsonContent (never sent as top-level attributes).
// SGP_JSON_FIELDS (incl. the URA keys sgpApp/sgpToken/sgpMode) comes from utils/erp.
const IXC_JSON_FIELDS = ["ixcUrl", "ixcToken", "tipoIntegracao", "timeoutMs"];
// Common to SGP/IXC: queue that receives the ticket when the customer sends a
// photo/PDF (receipt) while talking to the ERP chatbot.
const ERP_COMMON_JSON_FIELDS = ["receiptQueueId"];
const ISP_IA_JSON_FIELDS = [
  "erpIntegrationId",
  "openaiApiKey",
  "model",
  "promptExtra",
  "handoffQueueId",
  "maxTurns",
  "voice",
  "voiceMode",
];
// Hospedagem — conector (H1) e agente de IA (H2). baseUrl/apiKey are shared
// with HOTSPOT_JSON_FIELDS (same names, mutually exclusive by `type`).
const HOSPEDAGEM_CONECTOR_JSON_FIELDS = ["provider", "baseUrl", "apiKey", "propostaHabilitada"];
const HOSPEDAGEM_IA_JSON_FIELDS = [
  "hospedagemIntegrationId",
  "openaiApiKey",
  "model",
  "nomeAgente",
  "nomeEmpresa",
  "persona",
  "handoffQueueId",
  "maxTurns",
  "voice",
  "voiceMode",
  "permitirPreReserva",
  "pedirEmailParaReserva",
  // IA vendedora (Parte A): convertidos por vendedoraFromJson / applyVendedoraFields
  "responderPrimeiraMensagem",
  "fichaEmpresa",
  "escada",
  "periodosSemEscada",
  "incluirFeriadosNacionais",
];
const ALL_JSON_FIELDS = Array.from(
  new Set([
    ...SGP_JSON_FIELDS,
    ...IXC_JSON_FIELDS,
    ...ASAAS_JSON_FIELDS,
    ...ERP_COMMON_JSON_FIELDS,
    ...ISP_IA_JSON_FIELDS,
    ...HOTSPOT_JSON_FIELDS,
    ...HOSPEDAGEM_CONECTOR_JSON_FIELDS,
    ...HOSPEDAGEM_IA_JSON_FIELDS,
  ])
);

const parseJsonContent = (jsonContent) => {
  if (!jsonContent) return {};
  try {
    const parsed = JSON.parse(jsonContent);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    logError("Falha ao parsear jsonContent da integração", e);
    return {};
  }
};

const QueueIntegration = ({ open, onClose, integrationId }) => {
  const classes = useStyles();
  const panel = usePanelStyles();

  const initialState = {
    type: "typebot",
    name: "",
    projectName: "",
    jsonContent: "",
    language: "",
    urlN8N: "",
    typebotDelayMessage: 1000,
    typebotExpires: 1,
    typebotKeywordFinish: "",
    typebotKeywordRestart: "",
    typebotRestartMessage: "",
    typebotSlug: "",
    typebotUnknownMessage: "",
    // SGP integration fields
    sgpIeSenha: "",
    sgpUrl: "",
    sgpWidePayToken: "",
    // SGP — integração URA (app + token cadastrados no SGP); opcional
    sgpApp: "",
    sgpToken: "",
    sgpMode: SGP_DEFAULT_MODE,
    // SGP / IXC
    tipoIntegracao: "NA",
    receiptQueueId: "",
    // IXC integration fields
    ixcUrl: "",
    ixcToken: "",
    timeoutMs: "",
    // ASAAS (cobrança) fields
    asaasApiKey: "",
    asaasEnvironment: "production",
    // ISP_IA fields
    erpIntegrationId: "",
    openaiApiKey: "",
    model: ISP_IA_DEFAULTS.model,
    promptExtra: "",
    voice: "alloy",
    voiceMode: "mirror",
    handoffQueueId: "",
    maxTurns: ISP_IA_DEFAULTS.maxTurns,
    // HOTSPOT integration fields
    baseUrl: "",
    apiKey: "",
    defaultDeviceId: "",
    defaultTabela: "",
    // HOSPEDAGEM_CONECTOR fields (baseUrl/apiKey reused from HOTSPOT above)
    provider: HOSPEDAGEM_PROVIDERS[0]?.value || "alugue_no_litoral",
    propostaHabilitada: false,
    // HOSPEDAGEM_IA fields (openaiApiKey/model/handoffQueueId/maxTurns/voice/voiceMode reused from ISP_IA above)
    hospedagemIntegrationId: "",
    nomeAgente: "",
    nomeEmpresa: "",
    persona: "",
    permitirPreReserva: true,
    pedirEmailParaReserva: true,
    ...VENDEDORA_DEFAULTS,
  };

  const [integration, setIntegration] = useState(initialState);
  const [loadingData, setLoadingData] = useState(false);
  const [erpIntegrations, setErpIntegrations] = useState([]);
  const [queues, setQueues] = useState([]);
  const [testingErp, setTestingErp] = useState(false);
  // quais segredos estão visíveis (por nome do campo)
  const [shown, setShown] = useState({});
  const secret = (name) => ({
    visible: Boolean(shown[name]),
    onToggle: () => setShown((prev) => ({ ...prev, [name]: !prev[name] })),
  });
  const [testingHotspot, setTestingHotspot] = useState(false);
  const [hotspotDevices, setHotspotDevices] = useState([]);
  const [hospedagemIntegrations, setHospedagemIntegrations] = useState([]);
  const [testingHospedagem, setTestingHospedagem] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!integrationId) return;
      setLoadingData(true);
      try {
        const { data } = await api.get(`/queueIntegration/${integrationId}`);
        if (!active) return;
        const fromJson = parseJsonContent(data?.jsonContent);
        setIntegration((prevState) => {
          const next = { ...prevState, ...data };
          ALL_JSON_FIELDS.forEach((key) => {
            if (fromJson[key] !== undefined && fromJson[key] !== null) {
              next[key] = fromJson[key];
            }
          });
          if (!next.tipoIntegracao) next.tipoIntegracao = "NA";
          // escada/períodos têm forma própria no formulário (ex.: "DD/MM" no período anual)
          Object.assign(next, vendedoraFromJson(fromJson));
          next.propostaHabilitada = conectorPropostaFromJson(fromJson);
          if (data && data.type === "SGP") {
            const sgp = parseSgpJsonContent(data.jsonContent);
            next.sgpApp = sgp.sgpApp;
            next.sgpToken = sgp.sgpToken;
            next.sgpMode = sgp.sgpMode;
          } else {
            next.sgpMode = normalizeSgpMode(next.sgpMode);
          }
          if (!next.model) next.model = ISP_IA_DEFAULTS.model;
          if (next.maxTurns === "" || next.maxTurns === undefined) {
            next.maxTurns = ISP_IA_DEFAULTS.maxTurns;
          }
          return next;
        });
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoadingData(false);
      }
    })();

    return () => {
      active = false;
      setIntegration(initialState);
      setLoadingData(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, open]);

  // Lists used by the ISP_IA form (ERP integrations + queues for handoff)
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    (async () => {
      try {
        // GET /isp/erp-integrations returns every SGP/IXC integration of the
        // company (GET /queueIntegration is paginated to 20 rows).
        const { data } = await api.get("/isp/erp-integrations");
        if (active) setErpIntegrations(Array.isArray(data) ? data : []);
      } catch (err) {
        logError("Falha ao carregar integrações ERP", err);
      }
      try {
        const { data } = await api.get("/queue");
        if (active) setQueues(Array.isArray(data) ? data : []);
      } catch (err) {
        logError("Falha ao carregar filas", err);
      }
      try {
        // No dedicated "list hospedagem connectors" endpoint yet — reuse the
        // general integration list and filter HOSPEDAGEM_CONECTOR client-side.
        const { data } = await api.get("/queueIntegration");
        const list = data?.queueIntegrations || data || [];
        if (active) {
          setHospedagemIntegrations(
            (Array.isArray(list) ? list : []).filter(
              (it) => String(it?.type || "").toUpperCase() === "HOSPEDAGEM_CONECTOR"
            )
          );
        }
      } catch (err) {
        logError("Falha ao carregar conectores de hospedagem", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [open]);

  const handleClose = () => {
    onClose();
    setIntegration(initialState);
    setShown({});
  };

  const handleTestSession = async (event, values) => {
    try {
      const { projectName, jsonContent, language } = values;

      await api.post(`/queueIntegration/testSession`, {
        projectName,
        jsonContent,
        language,
      });

      toast.success(i18n.t("queueIntegrationModal.messages.testSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleTestErp = async () => {
    if (!integrationId) return;
    setTestingErp(true);
    try {
      const { data } = await api.post(
        `/queueIntegration/${integrationId}/erp-health`
      );
      const ok = data?.ok === true || data?.status === "ok";
      const latency =
        data?.latencyMs !== undefined && data?.latencyMs !== null
          ? `${data.latencyMs} ms`
          : data?.latency !== undefined && data?.latency !== null
          ? `${data.latency} ms`
          : "";
      const message = data?.message || data?.error || "";
      if (ok) {
        toast.success(
          `${i18n.t("queueIntegrationModal.messages.erpHealthOk")}${
            latency ? ` (${latency})` : ""
          }${message ? ` — ${message}` : ""}`
        );
      } else {
        toast.error(
          `${i18n.t("queueIntegrationModal.messages.erpHealthFail")}${
            latency ? ` (${latency})` : ""
          }${message ? ` — ${message}` : ""}`
        );
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTestingErp(false);
    }
  };

  const handleTestHotspot = async (values) => {
    setTestingHotspot(true);
    try {
      const { data } = await api.post("/hotspot/test", {
        baseUrl: values.baseUrl,
        apiKey: values.apiKey,
      });
      setHotspotDevices(Array.isArray(data?.devices) ? data.devices : []);
      const name = data?.tenant?.nome || data?.nome || "";
      toast.success(
        `${i18n.t("queueIntegrationModal.messages.hotspotTestOk", { name })}`
      );
    } catch (err) {
      toastError(err);
    } finally {
      setTestingHotspot(false);
    }
  };

  const handleTestHospedagem = async (values) => {
    setTestingHospedagem(true);
    try {
      const { data } = await api.post("/hospedagem/test", {
        provider: values.provider,
        baseUrl: values.baseUrl,
        apiKey: values.apiKey,
      });
      if (data?.ok) {
        toast.success(
          `${i18n.t("queueIntegrationModal.messages.hospedagemTestOk")}${
            data?.detail ? ` — ${data.detail}` : ""
          }`
        );
      } else {
        toast.error(
          `${i18n.t("queueIntegrationModal.messages.erpHealthFail")}${
            data?.detail ? ` — ${data.detail}` : ""
          }`
        );
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTestingHospedagem(false);
    }
  };

  const buildPayload = (values) => {
    const payload = { ...values };
    const baseJson = parseJsonContent(values.jsonContent);
    let jsonContent = null;

    if (values.type === "SGP") {
      // Empty URA fields are dropped from jsonContent (keeps old integrations intact)
      jsonContent = buildSgpJsonContent(values, baseJson);
    } else if (values.type === "IXC") {
      jsonContent = {
        ...baseJson,
        ixcUrl: values.ixcUrl,
        ixcToken: values.ixcToken,
        tipoIntegracao: values.tipoIntegracao || "NA",
      };
      if (values.timeoutMs !== "" && values.timeoutMs !== undefined && values.timeoutMs !== null) {
        jsonContent.timeoutMs = Number(values.timeoutMs);
      } else {
        delete jsonContent.timeoutMs;
      }
    } else if (values.type === "ASAAS") {
      jsonContent = {
        ...baseJson,
        asaasApiKey: values.asaasApiKey,
        asaasEnvironment: values.asaasEnvironment || "production",
        tipoIntegracao: values.tipoIntegracao || "NA",
      };
      if (values.timeoutMs !== "" && values.timeoutMs !== undefined && values.timeoutMs !== null) {
        jsonContent.timeoutMs = Number(values.timeoutMs);
      } else {
        delete jsonContent.timeoutMs;
      }
    } else if (values.type === "ISP_IA") {
      jsonContent = {
        ...baseJson,
        erpIntegrationId: values.erpIntegrationId
          ? Number(values.erpIntegrationId)
          : null,
        openaiApiKey: values.openaiApiKey,
        model: values.model || ISP_IA_DEFAULTS.model,
        promptExtra: values.promptExtra || "",
        handoffQueueId: values.handoffQueueId
          ? Number(values.handoffQueueId)
          : null,
        maxTurns:
          values.maxTurns !== "" && values.maxTurns !== undefined && values.maxTurns !== null
            ? Number(values.maxTurns)
            : ISP_IA_DEFAULTS.maxTurns,
        voice: values.voice || "alloy",
        voiceMode: values.voiceMode || "mirror",
      };
    } else if (values.type === "HOTSPOT") {
      jsonContent = {
        ...baseJson,
        baseUrl: values.baseUrl,
        apiKey: values.apiKey,
        defaultDeviceId:
          values.defaultDeviceId !== "" &&
          values.defaultDeviceId !== undefined &&
          values.defaultDeviceId !== null
            ? Number(values.defaultDeviceId)
            : null,
        defaultTabela: values.defaultTabela || "",
      };
    } else if (values.type === "HOSPEDAGEM_CONECTOR") {
      jsonContent = {
        ...baseJson,
        provider: values.provider || HOSPEDAGEM_PROVIDERS[0]?.value,
        baseUrl: values.baseUrl,
        apiKey: values.apiKey,
      };
      jsonContent = applyConectorPropostaField(jsonContent, values, baseJson);
    } else if (values.type === "HOSPEDAGEM_IA") {
      jsonContent = {
        ...baseJson,
        hospedagemIntegrationId: values.hospedagemIntegrationId
          ? Number(values.hospedagemIntegrationId)
          : null,
        openaiApiKey: values.openaiApiKey || "",
        model: values.model || HOSPEDAGEM_IA_DEFAULTS.model,
        nomeAgente: values.nomeAgente || "",
        nomeEmpresa: values.nomeEmpresa || "",
        persona: values.persona || "",
        handoffQueueId: values.handoffQueueId
          ? Number(values.handoffQueueId)
          : null,
        maxTurns:
          values.maxTurns !== "" && values.maxTurns !== undefined && values.maxTurns !== null
            ? Number(values.maxTurns)
            : HOSPEDAGEM_IA_DEFAULTS.maxTurns,
        voice: values.voice || HOSPEDAGEM_IA_DEFAULTS.voice,
        voiceMode: values.voiceMode || HOSPEDAGEM_IA_DEFAULTS.voiceMode,
        permitirPreReserva: values.permitirPreReserva !== false,
        pedirEmailParaReserva: values.pedirEmailParaReserva !== false,
      };
      jsonContent = applyVendedoraFields(jsonContent, values, baseJson);
    }

    if (jsonContent && ERP_TYPES.includes(values.type)) {
      if (values.receiptQueueId) jsonContent.receiptQueueId = Number(values.receiptQueueId);
      else delete jsonContent.receiptQueueId;
    }

    if (jsonContent) {
      payload.jsonContent = JSON.stringify(jsonContent);
    }
    // Avoid sending unknown top-level attrs (backend model lacks columns)
    ALL_JSON_FIELDS.forEach((key) => {
      delete payload[key];
    });
    return payload;
  };

  const handleSaveDialogflow = async (values) => {
    try {
      if (
        values.type === "n8n" ||
        values.type === "webhook" ||
        values.type === "typebot" ||
        values.type === "flowbuilder" ||
        values.type === "SGP" ||
        values.type === "IXC" ||
        values.type === "ASAAS" ||
        values.type === "ISP_IA" ||
        values.type === "HOTSPOT" ||
        values.type === "HOSPEDAGEM_CONECTOR" ||
        values.type === "HOSPEDAGEM_IA"
      ) {
        values.projectName = values.name;
      }

      let validationErrors = [];
      if (values.type === "SGP") validationErrors = validateSgpConfig(values);
      if (values.type === "IXC") validationErrors = validateIxcConfig(values);
      if (values.type === "ASAAS") validationErrors = validateAsaasConfig(values);
      if (values.type === "ISP_IA") validationErrors = validateIspIaConfig(values);
      if (values.type === "HOTSPOT") validationErrors = validateHotspotConfig(values);
      if (values.type === "HOSPEDAGEM_CONECTOR")
        validationErrors = validateHospedagemConectorConfig(values);
      if (values.type === "HOSPEDAGEM_IA") {
        validationErrors = validateHospedagemIaConfig(values);
        // degrau/período inválido seria descartado em silêncio pelo backend: não salva
        if (validateVendedora(values).hasErrors) {
          validationErrors.push(i18n.t("queueIntegrationModal.hospedagem.errors.fixFields"));
        }
      }
      if (validationErrors.length) {
        validationErrors.forEach((e) => toast.error(e));
        logError(`Falha de validação ${values.type}`, { errors: validationErrors });
        return;
      }
      if (
        [
          "SGP",
          "IXC",
          "ASAAS",
          "ISP_IA",
          "HOTSPOT",
          "HOSPEDAGEM_CONECTOR",
          "HOSPEDAGEM_IA",
        ].includes(values.type)
      ) {
        logInfo(`Salvando integração ${values.type}`, { name: values.name });
      }

      const payload = buildPayload(values);

      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, payload);
        toast.success(i18n.t("queueIntegrationModal.messages.editSuccess"));
      } else {
        await api.post("/queueIntegration", payload);
        toast.success(i18n.t("queueIntegrationModal.messages.addSuccess"));
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const t = (key) => i18n.t(`queueIntegrationModal.${key}`);

  const renderQueueOptions = (emptyLabel) => [
    <MenuItem key="" value="">
      {emptyLabel}
    </MenuItem>,
    ...queues.map((q) => (
      <MenuItem key={q.id} value={q.id}>
        {q.name}
      </MenuItem>
    )),
  ];

  const renderTestStrip = ({ hint, label, name, onClick, disabled, busy, tooltip }) => (
    <div className={classes.testStrip}>
      <div className={classes.testText}>
        <div className={classes.testTitle}>{t("buttons.testErp")}</div>
        <div className={panel.help}>{hint}</div>
      </div>
      <Tooltip title={tooltip || ""} placement="top">
        <span>
          <Button
            onClick={onClick}
            disabled={disabled}
            name={name}
            variant="outlined"
            startIcon={<SettingsEthernet />}
            className={panel.busyWrap}
          >
            {label}
            {busy && <CircularProgress size={20} className={panel.busy} />}
          </Button>
        </span>
      </Tooltip>
    </div>
  );

  // SGP / IXC / Asaas testam a integração JÁ SALVA (POST /queueIntegration/:id/erp-health)
  const renderErpTest = (isSubmitting) =>
    renderTestStrip({
      hint: integrationId
        ? "Confere o acesso ao ERP com as credenciais já salvas."
        : t("buttons.testErpSaveFirst"),
      label: t("buttons.testErp"),
      name: "testErp",
      onClick: handleTestErp,
      disabled: !integrationId || isSubmitting || testingErp,
      busy: testingErp,
      tooltip: integrationId ? "" : t("buttons.testErpSaveFirst"),
    });

  const renderReceiptSection = () => (
    <FormSection
      title="Comprovantes"
      description={t("form.receiptQueueIdHelp")}
    >
      <FormGrid>
        <SelectInput name="receiptQueueId" label={t("form.receiptQueueId")} id="receiptQueueId-selection">
          {renderQueueOptions(t("form.receiptQueueKeep"))}
        </SelectInput>
      </FormGrid>
    </FormSection>
  );

  const renderTipoIntegracao = () => (
    <SelectInput name="tipoIntegracao" label={t("form.tipoIntegracao")} id="tipoIntegracao-selection">
      {ERP_INTEGRATION_TYPES.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {i18n.t(opt.labelKey)}
        </MenuItem>
      ))}
    </SelectInput>
  );

  const renderTimeout = () => (
    <TextInput
      name="timeoutMs"
      label={t("form.timeoutMs")}
      type="number"
      inputProps={{ min: 1000, step: 500 }}
    />
  );

  const renderVoiceSection = (labels) => (
    <FormSection title="Voz">
      <FormGrid>
        <SelectInput name="voiceMode" label={labels.voiceMode} help={labels.voiceModeHelp}>
          <MenuItem value="mirror">{labels.mirror}</MenuItem>
          <MenuItem value="always">{labels.always}</MenuItem>
          <MenuItem value="off">{labels.off}</MenuItem>
        </SelectInput>
        <SelectInput name="voice" label={labels.voice}>
          {VOICES.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </SelectInput>
      </FormGrid>
    </FormSection>
  );

  const renderModelOptions = () =>
    ISP_IA_MODELS.map((opt) => (
      <MenuItem key={opt.value} value={opt.value}>
        {opt.label}
      </MenuItem>
    ));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="queue-integration-dialog-title"
    >
      <ModalTitle
        id="queue-integration-dialog-title"
        title={integrationId ? t("title.edit") : t("title.add")}
        subtitle="Conecte um ERP, um agente de IA ou um bot externo para usar nas filas e nos fluxos."
        onClose={handleClose}
      />
      <Formik
        initialValues={integration}
        enableReinitialize={true}
        validationSchema={DialogflowSchema}
        onSubmit={(values, actions, event) => {
          setTimeout(() => {
            handleSaveDialogflow(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ isSubmitting, values }) => (
          <Form className={panel.form}>
            {loadingData && <LinearProgress className={classes.loadingBar} />}
            <DialogContent>
              <FormSection>
                <FormGrid>
                  <SelectInput name="type" label={t("form.type")} id="type" required>
                    <ListSubheader disableSticky className={classes.groupHeader}>
                      ERP e cobrança
                    </ListSubheader>
                    <MenuItem value="SGP">{t("types.SGP")}</MenuItem>
                    <MenuItem value="IXC">{t("types.IXC")}</MenuItem>
                    <MenuItem value="ASAAS">{t("types.ASAAS")}</MenuItem>
                    <ListSubheader disableSticky className={classes.groupHeader}>
                      Agentes de IA e conectores
                    </ListSubheader>
                    <MenuItem value="ISP_IA">{t("types.ISP_IA")}</MenuItem>
                    <MenuItem value="HOTSPOT">{t("types.HOTSPOT")}</MenuItem>
                    <MenuItem value="HOSPEDAGEM_CONECTOR">
                      {t("types.HOSPEDAGEM_CONECTOR")}
                    </MenuItem>
                    <MenuItem value="HOSPEDAGEM_IA">{t("types.HOSPEDAGEM_IA")}</MenuItem>
                    <ListSubheader disableSticky className={classes.groupHeader}>
                      Bots e automação
                    </ListSubheader>
                    <MenuItem value="dialogflow">DialogFlow</MenuItem>
                    <MenuItem value="n8n">N8N</MenuItem>
                    <MenuItem value="webhook">WebHooks</MenuItem>
                    <MenuItem value="typebot">Typebot</MenuItem>
                    <MenuItem value="flowbuilder">Flowbuilder</MenuItem>
                  </SelectInput>
                  <TextInput
                    name="name"
                    label={t("form.name")}
                    autoFocus
                    required={
                      values.type !== "dialogflow" && values.type !== "flowbuilder"
                    }
                  />
                </FormGrid>
              </FormSection>

              {/* ===== SGP ===== */}
              {values.type === "SGP" && (
                <>
                  <FormSection title="Conexão com o SGP">
                    <FormGrid>
                      <TextInput name="sgpUrl" label={t("form.sgpUrl")} required />
                      {renderTipoIntegracao()}
                      <SelectInput
                        name="sgpIeSenha"
                        label={t("form.sgpIeSenha")}
                        id="sgpIeSenha-selection"
                        required
                      >
                        {SGP_PASSWORD_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </SelectInput>
                      <TextInput name="sgpWidePayToken" label={t("form.sgpWidePayToken")} />
                    </FormGrid>
                  </FormSection>
                  {/* SGP — integração URA (app + token) */}
                  <FormSection
                    title="Integração URA (app + token)"
                    description={t("form.sgpModeHelp")}
                  >
                    <FormGrid>
                      <TextInput
                        name="sgpApp"
                        label={t("form.sgpApp")}
                        autoComplete="off"
                        required={values.sgpMode === "ura"}
                      />
                      <SecretInput
                        name="sgpToken"
                        label={t("form.sgpToken")}
                        required={values.sgpMode === "ura"}
                        {...secret("sgpToken")}
                      />
                      <SelectInput name="sgpMode" label={t("form.sgpMode")} id="sgpMode-selection">
                        {SGP_MODES.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {i18n.t(opt.labelKey)}
                          </MenuItem>
                        ))}
                      </SelectInput>
                      {renderErpTest(isSubmitting)}
                    </FormGrid>
                  </FormSection>
                  {renderReceiptSection()}
                </>
              )}

              {/* ===== IXC ===== */}
              {values.type === "IXC" && (
                <>
                  <FormSection title="Conexão com o IXC">
                    <FormGrid>
                      <TextInput
                        name="ixcUrl"
                        label={t("form.ixcUrl")}
                        required
                        placeholder="https://ixc.seuprovedor.com.br"
                      />
                      <SecretInput
                        name="ixcToken"
                        label={t("form.ixcToken")}
                        required
                        {...secret("ixcToken")}
                      />
                      {renderTipoIntegracao()}
                      {renderTimeout()}
                      {renderErpTest(isSubmitting)}
                    </FormGrid>
                  </FormSection>
                  {renderReceiptSection()}
                </>
              )}

              {/* ===== ASAAS (cobrança) ===== */}
              {values.type === "ASAAS" && (
                <>
                  <FormSection
                    title="Conexão com o Asaas"
                    description={t("form.asaasHelp")}
                  >
                    <FormGrid>
                      <SecretInput
                        name="asaasApiKey"
                        label={t("form.asaasApiKey")}
                        required
                        {...secret("asaasApiKey")}
                      />
                      <SelectInput
                        name="asaasEnvironment"
                        label={t("form.asaasEnvironment")}
                        id="asaasEnvironment-selection"
                      >
                        {ASAAS_ENVIRONMENTS.map((env) => (
                          <MenuItem key={env.value} value={env.value}>
                            {env.label}
                          </MenuItem>
                        ))}
                      </SelectInput>
                      {renderTipoIntegracao()}
                      {renderTimeout()}
                      {renderErpTest(isSubmitting)}
                    </FormGrid>
                  </FormSection>
                  {renderReceiptSection()}
                </>
              )}

              {/* ===== ISP_IA ===== */}
              {values.type === "ISP_IA" && (
                <>
                  <FormSection title="ERP e modelo">
                    <FormGrid>
                      <SelectInput
                        name="erpIntegrationId"
                        label={t("form.erpIntegrationId")}
                        id="erpIntegrationId-selection"
                        required
                      >
                        <MenuItem value="">{t("form.none")}</MenuItem>
                        {erpIntegrations.map((it) => (
                          <MenuItem key={it.id} value={it.id}>
                            {it.name} ({it.type})
                          </MenuItem>
                        ))}
                      </SelectInput>
                      <SecretInput
                        name="openaiApiKey"
                        label={t("form.openaiApiKey")}
                        required
                        {...secret("openaiApiKey")}
                      />
                      <SelectInput name="model" label={t("form.model")} id="model-selection">
                        {renderModelOptions()}
                      </SelectInput>
                      <TextInput
                        name="maxTurns"
                        label={t("form.maxTurns")}
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </FormGrid>
                  </FormSection>
                  <FormSection title="Atendimento">
                    <FormGrid>
                      <SelectInput
                        name="handoffQueueId"
                        label={t("form.handoffQueueId")}
                        id="handoffQueueId-selection"
                      >
                        {renderQueueOptions(t("form.none"))}
                      </SelectInput>
                      <TextInput
                        full
                        name="promptExtra"
                        label={t("form.promptExtra")}
                        help={t("form.promptExtraHelp")}
                        multiline
                        minRows={4}
                        maxRows={10}
                      />
                    </FormGrid>
                  </FormSection>
                  {renderVoiceSection({
                    voiceMode: "Resposta em áudio",
                    voiceModeHelp: "Quando o agente responde por nota de voz",
                    mirror: "Quando o cliente mandar áudio (padrão)",
                    always: "Sempre que possível",
                    off: "Nunca (só texto)",
                    voice: "Voz (OpenAI TTS)",
                  })}
                </>
              )}

              {/* ===== HOTSPOT ===== */}
              {values.type === "HOTSPOT" && (
                <>
                  <FormSection
                    title="Conexão com o Hotspot"
                    description={t("form.hotspotHelp")}
                  >
                    <FormGrid>
                      <TextInput
                        name="baseUrl"
                        label={t("form.hotspotBaseUrl")}
                        required
                        placeholder="https://hotspot.seuprovedor.com.br"
                      />
                      <SecretInput
                        name="apiKey"
                        label={t("form.hotspotApiKey")}
                        required
                        {...secret("apiKey")}
                      />
                      {renderTestStrip({
                        hint: "Confere a URL e a chave informadas acima e carrega os equipamentos.",
                        label: t("form.hotspotTest"),
                        name: "testHotspot",
                        onClick: () => handleTestHotspot(values),
                        disabled:
                          isSubmitting ||
                          testingHotspot ||
                          !values.baseUrl ||
                          !values.apiKey,
                        busy: testingHotspot,
                      })}
                    </FormGrid>
                  </FormSection>
                  <FormSection title="Padrões das fichas">
                    <FormGrid>
                      <SelectInput
                        name="defaultDeviceId"
                        label={t("form.hotspotDefaultDevice")}
                        id="defaultDeviceId-selection"
                      >
                        <MenuItem value="">{t("form.none")}</MenuItem>
                        {hotspotDevices.map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.nome}
                          </MenuItem>
                        ))}
                      </SelectInput>
                      <SelectInput
                        name="defaultTabela"
                        label={t("form.hotspotDefaultTabela")}
                        id="defaultTabela-selection"
                      >
                        {HOTSPOT_TABELAS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {i18n.t(opt.labelKey)}
                          </MenuItem>
                        ))}
                      </SelectInput>
                    </FormGrid>
                  </FormSection>
                </>
              )}

              {/* ===== HOSPEDAGEM_CONECTOR (H1) ===== */}
              {values.type === "HOSPEDAGEM_CONECTOR" && (
                <FormSection title="Conexão com o site">
                  <FormGrid>
                    <SelectInput
                      name="provider"
                      label={t("form.hospedagemProvider")}
                      id="hospedagemProvider-selection"
                    >
                      {HOSPEDAGEM_PROVIDERS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </SelectInput>
                    <TextInput
                      name="baseUrl"
                      label={t("form.hospedagemBaseUrl")}
                      required
                      placeholder="https://alugue.exemplo.com.br"
                    />
                    <SecretInput
                      name="apiKey"
                      label={t("form.hospedagemApiKey")}
                      required
                      {...secret("apiKey")}
                    />
                    {renderTestStrip({
                      hint: "Confere a URL e a chave informadas acima, sem salvar.",
                      label: t("form.hospedagemTest"),
                      name: "testHospedagem",
                      onClick: () => handleTestHospedagem(values),
                      disabled:
                        isSubmitting ||
                        testingHospedagem ||
                        !values.baseUrl ||
                        !values.apiKey,
                      busy: testingHospedagem,
                    })}
                    <SwitchInput
                      full
                      name="propostaHabilitada"
                      label={t("form.hospedagemPropostaHabilitada")}
                      help={t("form.hospedagemPropostaHabilitadaHelp")}
                    />
                  </FormGrid>
                </FormSection>
              )}

              {/* ===== HOSPEDAGEM_IA (H2) ===== */}
              {values.type === "HOSPEDAGEM_IA" && (
                <>
                  <FormSection title="Conector e modelo">
                    <FormGrid>
                      <SelectInput
                        name="hospedagemIntegrationId"
                        label={t("form.hospedagemIntegrationId")}
                        id="hospedagemIntegrationId-selection"
                        required
                      >
                        <MenuItem value="">{t("form.none")}</MenuItem>
                        {hospedagemIntegrations.map((it) => (
                          <MenuItem key={it.id} value={it.id}>
                            {it.name}
                          </MenuItem>
                        ))}
                      </SelectInput>
                      <SecretInput
                        name="openaiApiKey"
                        label={t("form.openaiApiKey")}
                        help={t("form.openaiApiKeyOptionalHelp")}
                        {...secret("openaiApiKey")}
                      />
                      <SelectInput
                        name="model"
                        label={t("form.model")}
                        id="hospedagemModel-selection"
                      >
                        {renderModelOptions()}
                      </SelectInput>
                      <TextInput
                        name="maxTurns"
                        label={t("form.maxTurns")}
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </FormGrid>
                  </FormSection>
                  <FormSection title="Agente">
                    <FormGrid>
                      <TextInput name="nomeAgente" label={t("form.nomeAgente")} required />
                      <TextInput name="nomeEmpresa" label={t("form.nomeEmpresa")} required />
                      <SelectInput
                        name="handoffQueueId"
                        label={t("form.handoffQueueId")}
                        id="hospedagemHandoffQueueId-selection"
                      >
                        {renderQueueOptions(t("form.none"))}
                      </SelectInput>
                      <TextInput
                        full
                        name="persona"
                        label={t("form.persona")}
                        help={t("form.personaHelp")}
                        required
                        multiline
                        minRows={6}
                        maxRows={16}
                      />
                    </FormGrid>
                  </FormSection>
                  {renderVoiceSection({
                    voiceMode: t("form.voiceMode"),
                    voiceModeHelp: t("form.voiceModeHelp"),
                    mirror: t("form.voiceModeMirror"),
                    always: t("form.voiceModeAlways"),
                    off: t("form.voiceModeOff"),
                    voice: t("form.voice"),
                  })}
                  <FormSection title="Reservas">
                    <FormGrid>
                      <SwitchInput
                        name="permitirPreReserva"
                        label={t("form.permitirPreReserva")}
                      />
                      <SwitchInput
                        name="pedirEmailParaReserva"
                        label={t("form.pedirEmailParaReserva")}
                      />
                    </FormGrid>
                  </FormSection>
                  <FormSection title={t("hospedagem.vendaTitle")}>
                    <FormGrid>
                      <SwitchInput
                        full
                        name="responderPrimeiraMensagem"
                        label={t("hospedagem.responderPrimeiraMensagem")}
                        help={t("hospedagem.responderPrimeiraMensagemHelp")}
                      />
                      <TextInput
                        full
                        name="fichaEmpresa"
                        label={t("hospedagem.fichaEmpresa")}
                        help={t("hospedagem.fichaEmpresaHelp")}
                        multiline
                        minRows={5}
                        maxRows={16}
                        inputProps={{ "data-testid": "ficha-empresa" }}
                      />
                    </FormGrid>
                  </FormSection>
                  <HospedagemVendedoraFields />
                </>
              )}

              {values.type === "dialogflow" && (
                <FormSection title="Projeto DialogFlow">
                  <FormGrid>
                    <SelectInput
                      name="language"
                      label={t("form.language")}
                      id="language-selection"
                      required
                    >
                      <MenuItem value="pt-BR">Portugues</MenuItem>
                      <MenuItem value="en">Inglês</MenuItem>
                      <MenuItem value="es">Español</MenuItem>
                    </SelectInput>
                    <TextInput name="projectName" label={t("form.projectName")} />
                    <TextInput
                      full
                      name="jsonContent"
                      label={t("form.jsonContent")}
                      type="jsonContent"
                      multiline
                      maxRows={5}
                      minRows={5}
                    />
                  </FormGrid>
                </FormSection>
              )}

              {(values.type === "n8n" || values.type === "webhook") && (
                <FormSection title="Endereço">
                  <FormGrid columns={1}>
                    <TextInput name="urlN8N" label={t("form.urlN8N")} required />
                  </FormGrid>
                </FormSection>
              )}

              {values.type === "flowbuilder" && (
                <FormSection>
                  <Callout>
                    O Flowbuilder não precisa de outras configurações: informe só o nome.
                  </Callout>
                </FormSection>
              )}

              {values.type === "typebot" && (
                <>
                  <FormSection title="Typebot">
                    <FormGrid>
                      <TextInput full name="urlN8N" label={t("form.urlN8N")} required />
                      <TextInput name="typebotSlug" label={t("form.typebotSlug")} required />
                      <TextInput name="typebotExpires" label={t("form.typebotExpires")} />
                      <TextInput
                        name="typebotDelayMessage"
                        label={t("form.typebotDelayMessage")}
                      />
                    </FormGrid>
                  </FormSection>
                  <FormSection title="Palavras-chave e mensagens">
                    <FormGrid>
                      <TextInput
                        name="typebotKeywordFinish"
                        label={t("form.typebotKeywordFinish")}
                      />
                      <TextInput
                        name="typebotKeywordRestart"
                        label={t("form.typebotKeywordRestart")}
                      />
                      <TextInput
                        name="typebotUnknownMessage"
                        label={t("form.typebotUnknownMessage")}
                      />
                      <TextInput
                        name="typebotRestartMessage"
                        label={t("form.typebotRestartMessage")}
                      />
                    </FormGrid>
                  </FormSection>
                </>
              )}
            </DialogContent>

            <DialogActions>
              {values.type === "dialogflow" && (
                <Button
                  //type="submit"
                  onClick={(e) => handleTestSession(e, values)}
                  disabled={isSubmitting}
                  name="testSession"
                  variant="outlined"
                  className={panel.actionsLeft}
                >
                  {t("buttons.test")}
                </Button>
              )}
              <Button onClick={handleClose} disabled={isSubmitting}>
                {t("buttons.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={panel.busyWrap}
              >
                {integrationId ? t("buttons.okEdit") : t("buttons.okAdd")}
                {isSubmitting && (
                  <CircularProgress size={20} className={panel.busy} />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default QueueIntegration;
